/**
 * @bos/portal3 — "which models my runs use", on the account page.
 *
 * EVERY account sees this. Only an account an administrator has marked as allowed to choose
 * can change it — and that permission is checked ON THE SERVER, from the session's own user
 * id (`account.setModels` refuses with FORBIDDEN otherwise). This component reads
 * `models.mayChooseModels` from the same payload only to decide whether to render a form or
 * a sentence; hiding a control is never the guard.
 *
 * The read exists even for accounts that cannot write, because being told what your runs are
 * pointed at is not the same as being allowed to change it, and the first is worth having.
 *
 * THE CRITIC GUARD IS NOT OVERRIDABLE HERE. `account.setModels` passes `false` for the
 * override, hard-wired, and this form has no control that could ask for it — the choice to
 * accept a correlated critic union is a top-level administrator's, with the consequence
 * stated, and it is recorded against the account when they make it.
 */
import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { TRPCClientError } from '@trpc/client';
import { client } from '../rpc/client';

interface WireModelSelection {
  readonly think: string | null;
  readonly emit: string | null;
  readonly criticB: string | null;
}

interface WireStoredModels {
  readonly selection: WireModelSelection;
  readonly mayChooseModels: boolean;
  readonly criticCollapseAck: boolean;
  readonly setBy: 'admin' | 'account' | null;
  readonly updatedAt: string | null;
}

interface WireKnownModel {
  readonly spec: string;
  readonly provider: string;
  readonly label: string;
  readonly priced: boolean;
}

interface AccountModels {
  readonly models: WireStoredModels;
  readonly chainProviders: readonly string[];
  readonly knownModels: readonly WireKnownModel[];
}

const ROLE_LABEL = {
  think: 'Thinker',
  emit: 'Writer',
  criticB: 'Second critic',
} as const;

function errorMessage(err: unknown): string {
  if (err instanceof TRPCClientError) return err.message || 'Something went wrong.';
  return 'Could not reach the server. Please try again.';
}

export function ModelChoice() {
  const [data, setData] = useState<AccountModels | null>(null);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [think, setThink] = useState('');
  const [emit, setEmit] = useState('');
  const [criticB, setCriticB] = useState('');

  const load = useCallback(async () => {
    try {
      const r = (await client.account.models.query()) as AccountModels;
      setData(r);
      setThink(r.models.selection.think ?? '');
      setEmit(r.models.selection.emit ?? '');
      setCriticB(r.models.selection.criticB ?? '');
    } catch {
      setFailed(true);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // The same grade the server computes — shown before the request, decided by the request.
  const collapse = useMemo(() => {
    if (think.trim() === '' || criticB.trim() === '') return null;
    const p = (s: string) => {
      const i = s.indexOf(':');
      return i > 0
        ? { provider: s.slice(0, i).toLowerCase(), model: s.slice(i + 1).toLowerCase() }
        : { provider: '', model: s.toLowerCase() };
    };
    const a = p(think.trim());
    const b = p(criticB.trim());
    if (a.provider !== b.provider) return null;
    return a.model === b.model ? 'same-model' : 'same-provider';
  }, [think, criticB]);

  if (failed || data === null) return null; // a read that failed says nothing rather than guessing

  const sel = data.models.selection;
  const none = sel.think === null && sel.emit === null && sel.criticB === null;

  async function save(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await client.account.setModels.mutate({
        think: think.trim() === '' ? null : think.trim(),
        emit: emit.trim() === '' ? null : emit.trim(),
        criticB: criticB.trim() === '' ? null : criticB.trim(),
      });
      setSaved(true);
      await load();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card" data-testid="account-models">
      <h2 className="card__title">Models your runs use</h2>
      <p className="card__intro">
        {none
          ? 'Nothing is set for your account, so the factory’s own configuration decides which models run.'
          : data.models.setBy === 'account'
            ? 'You chose these.'
            : 'An administrator set these for your account.'}
      </p>

      <dl className="deflist" style={{ marginTop: '1.1rem' }}>
        {(['think', 'emit', 'criticB'] as const).map((role) => (
          <div className="deflist__row" key={role}>
            <dt className="deflist__key">{ROLE_LABEL[role]}</dt>
            <dd className="deflist__val deflist__val--muted">
              {sel[role] ?? 'the factory’s own choice'}
            </dd>
          </div>
        ))}
      </dl>

      {!data.models.mayChooseModels ? (
        <p className="form__hint" style={{ marginTop: '1rem' }}>
          Your administrator sets these. Ask them if you need a different one.
        </p>
      ) : (
        <form className="form" onSubmit={save} aria-label="Models" style={{ marginTop: '1.1rem' }}>
          {error !== null && (
            <p className="form__error" role="alert">
              {error}
            </p>
          )}
          {saved && (
            <p className="form__success" role="status">
              Saved. Your next run uses these; a run already going keeps the models it started
              with until it is resumed.
            </p>
          )}
          {(['think', 'emit', 'criticB'] as const).map((role) => {
            const value = role === 'think' ? think : role === 'emit' ? emit : criticB;
            const set = role === 'think' ? setThink : role === 'emit' ? setEmit : setCriticB;
            return (
              <label className="form__field" key={role}>
                <span className="form__label">{ROLE_LABEL[role]}</span>
                <input
                  className="form__input"
                  list="account-chain-models"
                  placeholder="leave empty — the factory decides"
                  value={value}
                  onChange={(e) => set(e.target.value)}
                  disabled={busy}
                />
              </label>
            );
          })}
          <datalist id="account-chain-models">
            {data.knownModels.map((m) => (
              <option value={m.spec} key={m.spec}>
                {m.label}
              </option>
            ))}
          </datalist>

          <p className="form__hint">
            The chain can call {data.chainProviders.join(' and ')}. Set the thinker and the second
            critic together or leave both empty — they are checked against each other, and the
            second critic may not run on the thinker’s provider: two arms that share a provider
            share their blind spots, and the union stops catching what it was built to catch.
          </p>
          {collapse !== null && (
            <p className="form__error">
              {collapse === 'same-model'
                ? 'The second critic cannot be the same model as the thinker.'
                : 'The second critic cannot run on the thinker’s provider. Only an administrator can override that.'}
            </p>
          )}

          <div className="form__actions">
            <button className="btn btn--primary" type="submit" disabled={busy}>
              {busy ? 'Saving…' : 'Save models'}
            </button>
            <button
              className="btn btn--quiet"
              type="button"
              disabled={busy}
              onClick={() => {
                setThink('');
                setEmit('');
                setCriticB('');
              }}
            >
              Back to the factory’s choice
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
